import { HQ_BASE } from '../constants';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hqAuthService } from '../services/hqAuthService';
import { hqDashboardService, HqDashboardStats } from '../services/hqStoreService';
import hqAdmissionService, { AdmissionStats } from '../services/hqAdmissionService';

const HqDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const user = hqAuthService.getCachedUser();
  const [stats, setStats] = useState<HqDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [admStats, setAdmStats] = useState<AdmissionStats | null>(null);
  const [admMedicalCount, setAdmMedicalCount] = useState<number>(0);
  const [admAiBioCount, setAdmAiBioCount] = useState<number>(0);

  useEffect(() => {
    hqDashboardService.stats()
      .then(setStats)
      .catch(() => {})
      .finally(() => setLoading(false));

    // 입시 통계
    hqAdmissionService.stats().then(setAdmStats).catch(() => {});
    hqAdmissionService.medicalTrend().then(d => setAdmMedicalCount(d.total || 0)).catch(() => {});
    hqAdmissionService.aiBioNew().then(d => setAdmAiBioCount(d.new_depts?.length || 0)).catch(() => {});
  }, []);

  const fmt = (n: number) => n.toLocaleString('ko-KR');
  const fmtMan = (n: number) => `${(n / 10000).toLocaleString('ko-KR', { maximumFractionDigits: 0 })}만`;

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold text-slate-800 mb-6">📊 대시보드</h1>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-3">환영합니다 👋</h2>
        <p className="text-slate-600">
          <strong>{user?.name}</strong> 님 ({user?.role === 'hq_admin' ? '본부 관리자' : '본부 직원'})
        </p>
        <p className="text-slate-500 text-sm mt-1">
          소속: {user?.brand_name || '-'} · 정산월: {stats?.summary.settlement_month || '-'}
        </p>
      </div>

      {/* KPI 4개 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Kpi
          label="가맹점 수"
          value={loading ? '...' : `${stats?.summary.active_stores ?? 0} / ${stats?.summary.target_stores ?? 50}`}
          sub={loading ? '' : `가맹점 ${stats?.summary.franchisee_count} · 직영 ${stats?.summary.direct_count}`}
        />
        <Kpi
          label="전체 학생"
          value={loading ? '...' : `${fmt(stats?.summary.total_students ?? 0)}명`}
          sub="모든 매장 합계"
        />
        <Kpi
          label="이번달 예상 로열티"
          value={loading ? '...' : fmtMan(stats?.summary.estimated_monthly_royalty ?? 0) + '원'}
          sub="현재 학생 수 기준"
        />
        <Kpi
          label="목표까지"
          value={loading ? '...' : `${50 - (stats?.summary.active_stores ?? 0)}개`}
          sub="가맹점 50개 목표"
        />
      </div>

      {/* 입시 컨설팅 차별화 카드 */}
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="font-semibold text-indigo-900">🎓 입시 컨설팅 (본부 차별화)</h2>
            <p className="text-xs text-indigo-700 mt-0.5">
              adiga.kr 5년 누적 데이터 + 자동 트렌드 분석
            </p>
          </div>
          <button
            onClick={() => navigate(`${HQ_BASE}/admission/match`)}
            className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 text-sm"
          >
            학생 매칭 →
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <MiniStat label="등록 학과" value={admStats?.total_records ?? 0} suffix="개" />
          <MiniStat label="대학" value={admStats?.universities ?? 0} suffix="개" />
          <MiniStat label="🏥 의대 변경" value={admMedicalCount} suffix="건 (5년)" highlight />
          <MiniStat label="🤖 AI/바이오 신설" value={admAiBioCount} suffix="개" highlight />
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => navigate(`${HQ_BASE}/admission/insights`)}
            className="text-sm text-indigo-700 hover:underline"
          >
            📈 5년 인사이트 보기 →
          </button>
        </div>
      </div>

      {/* Top 매장 */}
      <div className="bg-white rounded-xl shadow overflow-hidden">
        <div className="px-5 py-4 border-b flex justify-between items-center">
          <h2 className="font-semibold">🏪 매장별 현황 (Top 10)</h2>
          <button
            onClick={() => navigate(`${HQ_BASE}/stores`)}
            className="text-sm text-blue-600 hover:underline"
          >
            전체 보기 →
          </button>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-3 py-2 text-left">매장</th>
              <th className="px-3 py-2 text-right">좌석</th>
              <th className="px-3 py-2 text-right">학생</th>
              <th className="px-3 py-2 text-right">점유율</th>
              <th className="px-3 py-2 text-right">이번달 로열티</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center text-slate-400">불러오는 중...</td>
              </tr>
            ) : (stats?.top_stores.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-slate-400">
                  등록된 가맹점이 없습니다.{' '}
                  <button onClick={() => navigate(`${HQ_BASE}/stores/new`)} className="text-blue-600 hover:underline">
                    + 첫 가맹점 등록
                  </button>
                </td>
              </tr>
            ) : (
              stats?.top_stores.map((s) => (
                <tr key={s.id} className="border-b last:border-b-0 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`${HQ_BASE}/stores/${s.id}`)}>
                  <td className="px-3 py-2 font-medium text-slate-800">{s.name}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{s.seat_count ?? '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold">{s.student_count}명</td>
                  <td className="px-3 py-2 text-right">{s.occupancy_rate != null ? `${s.occupancy_rate}%` : '-'}</td>
                  <td className="px-3 py-2 text-right font-semibold text-slate-700">
                    {fmtMan(s.royalty_amount)}원
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

const Kpi: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <div className="bg-white rounded-xl shadow p-5">
    <div className="text-sm text-slate-500">{label}</div>
    <div className="text-xl sm:text-2xl font-bold text-slate-800 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const MiniStat: React.FC<{ label: string; value: number; suffix?: string; highlight?: boolean }> = ({ label, value, suffix, highlight }) => (
  <div className={`p-3 rounded-lg ${highlight ? 'bg-white border border-indigo-200' : 'bg-white/60'}`}>
    <div className="text-xs text-slate-600">{label}</div>
    <div className={`text-xl font-bold ${highlight ? 'text-indigo-700' : 'text-slate-800'}`}>
      {value.toLocaleString()}
      <span className="text-xs font-normal text-slate-500 ml-1">{suffix}</span>
    </div>
  </div>
);

export default HqDashboardPage;
